// src/app/components/DrofusSettingsCard.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Stack,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Alert,
  Badge,
  Switch,
  Code,
  Accordion,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { openapi } from "../../openapi";
import { useProjectId } from "../hooks/useProjectId";
import { useAppSelector } from "../app/hooks";
import { selectHttpCurrentProjct } from "../projectSelectors";

type FormValues = {
  drofusBaseUrl: string;
  drofusDb: string;
  drofusProject: string;
  apiKey?: string;
  verify: boolean;
};

const minKeyLen = 6;

export const DrofusSettingsCard: React.FC = () => {
  const projectNo = useProjectId();
  const project = useAppSelector(selectHttpCurrentProjct);

  // Lagre/oppdatere innstillinger
  const [
    updateSettings,
    {
      isLoading: isSaving,
      isSuccess: isSaveSuccess,
      data: saveData,
      isError: isSaveError,
      error: saveError,
    },
  ] = openapi.endpoints.postProjectDrofusUpdateSettings.useMutation();

  // Test tilkobling (endpoint svarer 200 med ok:true|false)
  const [verifyConn, { isLoading: isVerifying }] =
    openapi.endpoints.postProjectDrofusVerifyConnection.useMutation();

  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);
  const [verifyOk, setVerifyOk] = useState<{ ms?: number; last4?: string } | null>(null);

  // Offentlig prosjekt-modell har hasApiKey/last4/verifiedAt
  const hasStoredKey = !!project?.drofus?.hasApiKey;

  const savedLast4 = saveData?.last4 ?? project?.drofus?.last4;
  const savedVerifiedAt = saveData?.verifiedAt ?? project?.drofus?.verifiedAt;

  const verifiedText = useMemo(() => {
    if (!savedVerifiedAt) return undefined;
    try {
      return new Date(savedVerifiedAt).toLocaleString();
    } catch {
      return String(savedVerifiedAt);
    }
  }, [savedVerifiedAt]);

  const form = useForm<FormValues>({
    initialValues: {
      drofusBaseUrl: "https://api-no.drofus.com/api",
      drofusDb: "",
      drofusProject: "",
      apiKey: "",
      verify: true,
    },
    validate: {
      drofusBaseUrl: (v) => (/^https?:\/\/.+/i.test(v) ? null : "Ugyldig URL"),
      drofusDb: (v) => (v ? null : "Påkrevd"),
      drofusProject: (v) => (v ? null : "Påkrevd"),
      apiKey: (v) => {
        // Påkrevd første gang (hvis ingen lagret nøkkel finnes)
        if (!hasStoredKey && (!v || v.trim().length < minKeyLen)) {
          return `Påkrevd første gang (minst ${minKeyLen} tegn)`;
        }
        if (v && v.trim().length < minKeyLen) return `For kort nøkkel (minst ${minKeyLen})`;
        return null;
      },
    },
  });

  // Hydrer felter når prosjektet endrer seg
  useEffect(() => {
    const p = project?.drofus;
    if (!p) return;
    form.setValues((prev) => ({
      ...prev,
      drofusBaseUrl: p.drofusBaseUrl || "https://api-no.drofus.com/api",
      drofusDb: (p as any).drofusDb || "",
      drofusProject: p.drofusProject || "",
      apiKey: "",
    }));
    setVerifyMsg(null);
    setVerifyOk(null);
  }, [project?.drofus]);

  const handleVerifyOnly = async () => {
    setVerifyMsg(null);
    setVerifyOk(null);

    // Krev nøkkel ved test hvis ingen lagret
    if (!hasStoredKey && !form.values.apiKey?.trim()) {
      form.setFieldError("apiKey", `Påkrevd for test uten lagret nøkkel (minst ${minKeyLen} tegn)`);
      return;
    }
    //postProjectDrofusVerifyConnection
    const res = await verifyConn({
      body: {
        projectNo,
      },
    }).unwrap();

    if (res.ok) {
      setVerifyOk({ ms: res.elapsedMs, last4: res.last4 });
    } else {
      const e = res.error;
      setVerifyMsg(
        e?.message ??
          (e?.origin === "upstream"
            ? "dRofus svarte med feil."
            : e?.origin === "network"
              ? "Nettverksfeil."
              : "Ukjent feil.")
      );
    }
  };

  const handleSave = async () => {
    setVerifyMsg(null);
    setVerifyOk(null);

    const validate = form.validate();
    if (validate.hasErrors) return;

    await updateSettings({
      body: {
        projectNo,
        settings: {
          drofusBaseUrl: form.values.drofusBaseUrl.trim(),
          drofusDb: form.values.drofusDb.trim(),
          drofusProject: form.values.drofusProject.trim(),
          apiKey: form.values.apiKey?.trim() || undefined, // tomt => behold eksisterende nøkkel
        },
      },
    }).unwrap();

    // Nullstill plaintext i UI etter lagring
    form.setFieldValue("apiKey", "");
  };

  const disableVerify =
    isVerifying ||
    !form.values.drofusBaseUrl ||
    !form.values.drofusDb ||
    !form.values.drofusProject;

  const disableSave =
    isSaving ||
    !form.values.drofusBaseUrl ||
    !form.values.drofusDb ||
    !form.values.drofusProject ||
    (!hasStoredKey && !form.values.apiKey?.trim());

  return (
    <Card withBorder radius="lg" p="md">
      <Stack gap="sm">
        <Text fw={600}>dRofus</Text>
        <Text size="sm" c="dimmed">
          Konfigurer tilkobling til dRofus for dette prosjektet (<Code>{projectNo}</Code>).
          API-nøkkelen lagres <i>kryptert</i> på serveren; vi viser kun de siste 4 tegnene.
        </Text>

        <Alert color="blue" variant="light">
          <Stack gap={4}>
            <Text size="sm">Vi behandler API-nøkkelen trygt:</Text>
            <Text size="sm" c="dimmed">
              • Sendes kun over HTTPS (TLS) til vår server
            </Text>
            <Text size="sm" c="dimmed">
              • Lagring skjer kryptert (AES-256-GCM) – vi viser kun de siste 4 tegnene
            </Text>
            <Text size="sm" c="dimmed">
              • Aldri logget eller delt med tredjeparter
            </Text>
            <Text size="sm" c="dimmed">
              • Brukes kun til å hente data fra dRofus for dette prosjektet
            </Text>
          </Stack>
        </Alert>

        <Accordion variant="separated" radius="md">
          <Accordion.Item value="security-details">
            <Accordion.Control>Hvordan vi beskytter nøkkelen</Accordion.Control>
            <Accordion.Panel>
              <Stack gap={6}>
                <Text size="sm">
                  • Nøkkelen krypteres på server med egen nøkkel som oppbevares separat.
                </Text>
                <Text size="sm">
                  • Vi viser aldri nøkkelen i klartekst i grensesnittet – kun <b>••••1234</b>.
                </Text>
                <Text size="sm">
                  • Tilgangen er adgangskontrollert; kun autoriserte administratorer kan endre
                  tilkoblingen.
                </Text>
                <Text size="sm">
                  • Du kan rotere/slette nøkkelen når som helst ved å legge inn en ny eller tømme
                  feltet.
                </Text>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        {isSaveError && (
          <Alert color="red" title="Kunne ikke lagre" variant="light">
            {(saveError as any)?.data?.message ||
              (saveError as any)?.message ||
              "Det oppstod en feil under oppdatering av innstillinger."}
          </Alert>
        )}
        {isSaveSuccess && (
          <Alert color="green" title="Lagret" variant="light">
            Innstillinger oppdatert
            {savedLast4 ? <> – nøkkel: ••••{savedLast4}</> : null}
            {verifiedText ? (
              <>
                {" "}
                · sist verifisert: <b>{verifiedText}</b>
              </>
            ) : null}
          </Alert>
        )}
        {verifyMsg && (
          <Alert
            color="red"
            title="Test feilet"
            variant="light"
            withCloseButton
            onClose={() => setVerifyMsg(null)}
          >
            {verifyMsg}
          </Alert>
        )}
        {verifyOk && (
          <Alert
            color="green"
            title="Test OK"
            variant="light"
            withCloseButton
            onClose={() => setVerifyOk(null)}
          >
            Tilkobling OK{verifyOk.last4 ? <> – nøkkel: ••••{verifyOk.last4}</> : null}
            {typeof verifyOk.ms === "number" ? <> · {verifyOk.ms}ms</> : null}
          </Alert>
        )}

        <TextInput
          label="Base URL"
          placeholder="https://api-no.drofus.com/api"
          {...form.getInputProps("drofusBaseUrl")}
        />
        <Group grow align="flex-start">
          <TextInput
            label="Database"
            placeholder="rikshospitalet"
            {...form.getInputProps("drofusDb")}
          />
          <TextInput label="Prosjekt" placeholder="01" {...form.getInputProps("drofusProject")} />
        </Group>

        <PasswordInput
          label={`API-nøkkel${hasStoredKey ? " (valgfritt)" : " (påkrevd første gang)"}`}
          description={
            hasStoredKey ? "La feltet stå tomt for å beholde eksisterende nøkkel." : undefined
          }
          placeholder="••••••••••••"
          autoComplete="new-password"
          {...form.getInputProps("apiKey")}
        />

        <Group justify="space-between" align="center">
          <Group gap="xs">
            {savedLast4 ? (
              <Badge variant="light" color="blue">
                Lagret nøkkel: ••••{savedLast4}
              </Badge>
            ) : (
              <Badge variant="light" color="gray">
                Ingen nøkkel lagret
              </Badge>
            )}
            {verifiedText && (
              <Badge variant="light" color="green">
                Verifisert
              </Badge>
            )}
          </Group>
          <Switch
            label="Verifiser etter lagring"
            checked={form.values.verify}
            onChange={(e) => form.setFieldValue("verify", e.currentTarget.checked)}
          />
        </Group>

        <Group justify="flex-end" mt="xs">
          <Button
            type="button"
            variant="default"
            onClick={handleVerifyOnly}
            loading={isVerifying}
            disabled={disableVerify}
          >
            Test tilkobling
          </Button>
          <Button type="button" onClick={handleSave} loading={isSaving} disabled={disableSave}>
            Lagre
          </Button>
        </Group>
      </Stack>
    </Card>
  );
};

export default DrofusSettingsCard;
